/**
 * Scanner / BT wedge helpers.
 * Hidden text inputs steal focus on phones and open the virtual keyboard.
 * Use passive key capture on touch layouts; never auto-focus hidden inputs on mobile.
 */

export const MOBILE_LAYOUT_MAX = 767;

export function isMobileLayout() {
    if (typeof window === 'undefined') return false;
    const w = window.visualViewport?.width ?? window.innerWidth;
    return w <= MOBILE_LAYOUT_MAX;
}

export function isTouchPrimaryDevice() {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(pointer: coarse)').matches;
}

/** True when auto-focusing a hidden scanner input would likely open the virtual keyboard. */
export function shouldAvoidScannerInputFocus() {
    return isMobileLayout() || isTouchPrimaryDevice();
}

export function isScanSuspended() {
    if (typeof document === 'undefined') return false;
    return !!document.querySelector('[data-scan-suspend="true"]');
}

export function isInteractiveEl(el) {
    if (!el || typeof el.tagName !== 'string') return false;
    const tag = el.tagName.toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON') return true;
    const role = (el.getAttribute && el.getAttribute('role')) || '';
    if (role === 'button' || role === 'listbox' || role === 'combobox' || role === 'textbox') return true;
    if (el.isContentEditable) return true;
    return false;
}

export function isUserTypingInField() {
    const active = document.activeElement;
    if (!active || active === document.body || active === document.documentElement) return false;
    return isInteractiveEl(active);
}

/** Safe props for hidden wedge inputs (still receive BT keystrokes on many desktop browsers when readOnly). */
export function scannerHiddenInputProps() {
    const avoidFocus = shouldAvoidScannerInputFocus();
    return {
        readOnly: avoidFocus,
        inputMode: avoidFocus ? 'none' : 'text',
        autoComplete: 'off',
        autoCorrect: 'off',
        autoCapitalize: 'off',
        spellCheck: false,
    };
}

const WEDGE_MAX_GAP_MS = 120;
const WEDGE_MIN_LENGTH = 3;

/**
 * Capture BT wedge scans via keydown without focusing an input (mobile-safe).
 * @returns {() => void} cleanup
 */
export function installKeyboardWedgeCapture({ enabled, onScan, isSuspended = isScanSuspended }) {
    let buffer = '';
    let lastKeyTime = 0;

    const onKeyDown = (e) => {
        if (typeof enabled === 'function' ? !enabled() : !enabled) return;
        if (isSuspended()) return;
        if (isUserTypingInField()) {
            buffer = '';
            return;
        }
        if (e.ctrlKey || e.metaKey || e.altKey) return;

        const now = Date.now();
        if (now - lastKeyTime > WEDGE_MAX_GAP_MS) buffer = '';
        lastKeyTime = now;

        if (e.key === 'Enter') {
            const code = buffer.trim();
            buffer = '';
            if (code.length >= WEDGE_MIN_LENGTH) {
                e.preventDefault();
                e.stopPropagation();
                onScan(code);
            }
            return;
        }

        if (e.key.length === 1) {
            buffer += e.key;
            // Stop bubble-phase shortcuts (e.g. Packers 1–6 / Q) from stealing wedge digits
            e.preventDefault();
            e.stopPropagation();
        }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
        window.removeEventListener('keydown', onKeyDown, true);
        buffer = '';
    };
}

/**
 * Desktop-only sticky focus for a scanner input ref. No-op on mobile / touch.
 * @returns {() => void} cleanup
 */
export function installScannerFocusLoop(getEl, { enabled = () => true, intervalMs = 800 } = {}) {
    if (shouldAvoidScannerInputFocus()) {
        return () => {};
    }

    const tick = () => {
        if (!enabled()) return;
        if (isScanSuspended()) return;
        const el = typeof getEl === 'function' ? getEl() : getEl;
        if (!el) return;
        const active = document.activeElement;
        const typingElsewhere = active && active !== el && isInteractiveEl(active);
        if (typingElsewhere) return;
        if (active !== el && typeof el.focus === 'function') {
            el.focus({ preventScroll: true });
        }
    };

    tick();
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
}
